import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";

const client = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
client.interceptors.request.use((c) => {
  const t = localStorage.getItem("tt_portal_token");
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
client.interceptors.response.use((r) => r, (err) => {
  // Only bounce to /portal/login when the user is ALREADY inside the portal on a protected route
  // (never redirect from the public marketing site — that hijacked the homepage).
  const p = window.location.pathname;
  const insideProtectedPortal =
    p.startsWith("/portal") &&
    !p.startsWith("/portal/login") &&
    !p.startsWith("/portal/register") &&
    !p.startsWith("/portal/join");
  if (err?.response?.status === 401 && insideProtectedPortal) {
    localStorage.removeItem("tt_portal_token");
    window.location.href = "/portal/login";
  }
  return Promise.reject(err);
});

export const portalApi = {
  register: (data) => client.post("/portal/auth/register", data),
  login: (email, password) => client.post("/portal/auth/login", { email, password }),
  me: () => client.get("/portal/auth/me"),
  logout: () => client.post("/portal/auth/logout"),
  subscribe: (tier, origin_url) => client.post("/portal/subscribe", { tier, origin_url }),
  subscribeStatus: (session_id) => client.get(`/portal/subscribe/status/${session_id}`),
  pricing: () => client.get("/portal/pricing"),
  listShipments: () => client.get("/portal/shipments"),
  getRates: (payload) => client.post("/portal/rates", payload),
  buyLabel: (ep_shipment_id, rate_id) => client.post(`/portal/shipments/${ep_shipment_id}/buy`, { rate_id }),
  createPickup: (payload) => client.post("/portal/pickups", payload),
  buyPickup: (payload) => client.post("/portal/pickups/buy", payload),
  labelUrl: (ep_shipment_id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/label/${ep_shipment_id}`,
  // Bulk CSV (Business tier)
  bulkUpload: (file, from_address) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/portal/bulk/upload?from_address_json=${encodeURIComponent(JSON.stringify(from_address))}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  bulkRate: (batch_id) => client.post(`/portal/bulk/${batch_id}/rate`),
  bulkBuy: (batch_id, payload) => client.post(`/portal/bulk/${batch_id}/buy`, payload),
  bulkGet: (batch_id) => client.get(`/portal/bulk/${batch_id}`),
  bulkList: () => client.get("/portal/bulk"),
  bulkPickup: (batch_id, payload) => client.post(`/portal/bulk/${batch_id}/pickup`, payload),
  bulkZipUrl: (batch_id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/bulk/${batch_id}/labels.zip`,
  // Address Book
  listAddresses: (kind) => client.get("/portal/addresses", { params: kind ? { kind } : {} }),
  createAddress: (data) => client.post("/portal/addresses", data),
  updateAddress: (id, data) => client.patch(`/portal/addresses/${id}`, data),
  deleteAddress: (id) => client.delete(`/portal/addresses/${id}`),
  // Return labels
  returnRates: (ep_shipment_id) => client.post(`/portal/shipments/${ep_shipment_id}/return-rates`),
  buyReturn: (ep_shipment_id, rate_id) => client.post(`/portal/shipments/${ep_shipment_id}/return-buy`, { rate_id }),
  returnLabelUrl: (ep_shipment_id) => `${process.env.REACT_APP_BACKEND_URL}/api/portal/label/${ep_shipment_id}/return`,
  // Team / multi-user seats
  team: () => client.get("/portal/team"),
  inviteMember: (email) => client.post("/portal/team/invite", { email }),
  cancelInvite: (invite_id) => client.delete(`/portal/team/invites/${invite_id}`),
  removeMember: (cid) => client.delete(`/portal/team/members/${cid}`),
  transferOwnership: (cid) => client.post(`/portal/team/transfer-ownership/${cid}`),
  lookupInvite: (token) => client.get(`/portal/team/invites/${token}`),
  acceptInvite: (token) => client.post("/portal/team/accept", { token }),
  // Delivery email templates
  getTemplates: () => client.get("/portal/templates"),
  saveTemplates: (data) => client.put("/portal/templates", data),
  previewTemplate: (subject, body) => client.post("/portal/templates/preview", { subject, body }),
  aiRewriteTemplate: (status, tone, current_body) => client.post("/portal/templates/ai-rewrite", { status, tone, current_body }),
  // Google Sign-In (managed OAuth)
  googleSession: (session_id) => client.post("/portal/auth/google/session", { session_id }),
  // Notifications
  notifications: (params = {}) => client.get("/portal/notifications", { params }),
  markNotificationsRead: (ids) => client.post("/portal/notifications/read", { ids: ids || null }),
  // Shipper (Fulfillment) — enroll & manage
  shipperProfile: () => client.get("/shipper/profile"),
  shipperEnroll: (data) => client.post("/shipper/enroll", data),
  shipperUpdate: (data) => client.patch("/shipper/profile", data),
  shipperCreateASN: (data) => client.post("/shipper/asns", data),
  shipperListASNs: () => client.get("/shipper/asns"),
  shipperInventory: () => client.get("/shipper/inventory"),
  shipperOrders: () => client.get("/shipper/orders"),
};

const Ctx = createContext({ customer: undefined });
export const PortalAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(undefined);
  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback (in App.js) will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setCustomer(null);
      return;
    }
    // Prefer JWT (email/password login) — call /me if we have a stored token OR a Google session cookie.
    // Try /me either way to auto-restore Google sessions on page refresh.
    portalApi.me().then((r) => setCustomer(r.data.customer)).catch(() => setCustomer(null));
  }, []);
  const login = async (email, pw) => {
    const r = await portalApi.login(email, pw);
    localStorage.setItem("tt_portal_token", r.data.token);
    setCustomer(r.data.customer);
    return r.data.customer;
  };
  const register = async (data) => {
    const r = await portalApi.register(data);
    localStorage.setItem("tt_portal_token", r.data.token);
    setCustomer(r.data.customer);
    return r.data.customer;
  };
  const logout = async () => { await portalApi.logout().catch(() => {}); localStorage.removeItem("tt_portal_token"); setCustomer(null); };
  return <Ctx.Provider value={{ customer, setCustomer, login, register, logout }}>{children}</Ctx.Provider>;
};
export const usePortalAuth = () => useContext(Ctx);

export const PortalGuard = ({ children }) => {
  const { customer } = usePortalAuth();
  const loc = useLocation();
  if (customer === undefined) return <div className="min-h-screen grid place-items-center bg-[var(--tt-bg)] text-sm text-[var(--tt-muted)]">Loading…</div>;
  if (customer === null) return <Navigate to="/portal/login" state={{ from: loc }} replace />;
  return children;
};
